import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Github, ArrowRight } from "lucide-react";
import { projects, accentTokens } from "../data/mock";

const ProjectCard = ({ project, index }) => {
  const reverse = index % 2 === 1;
  const t = accentTokens[project.accent] || accentTokens.cyan;
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative gradient-border rounded-3xl glass overflow-hidden transition-all duration-500 hover:-translate-y-1 ${t.glow} hover:shadow-[0_20px_60px_-15px_rgba(168,85,247,0.45)]`}
      data-testid={`project-card-${project.id}`}
    >
      {/* corner accent blob */}
      <div className={`absolute -top-24 ${reverse ? "-left-24" : "-right-24"} h-64 w-64 ${t.blob} blur-3xl rounded-full pointer-events-none opacity-60`} />

      <div className={`relative grid lg:grid-cols-12 ${reverse ? "lg:[direction:rtl]" : ""}`}>
        {/* Image */}
        <div className="lg:col-span-5 relative overflow-hidden [direction:ltr]">
          <div className="aspect-[4/3] lg:aspect-auto lg:h-full relative">
            <img
              src={project.image}
              alt={project.title}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
            <div className={`absolute inset-0 bg-gradient-to-tr from-[#07061A]/80 via-${project.accent === "cyan" ? "cyan" : project.accent === "pink" ? "pink" : project.accent === "indigo" ? "indigo" : "violet"}-900/30 to-transparent`} />
            <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-2.5 py-1 rounded-full glass-strong">
              <span className={`h-1.5 w-1.5 rounded-full bg-gradient-to-br ${t.ring}`} />
              <span className={`font-mono text-[10px] uppercase tracking-widest ${t.text}`}>
                {project.category}
              </span>
            </div>
            <div className="absolute bottom-5 left-5 font-mono text-6xl font-bold text-white/15">
              {project.index}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-7 p-6 sm:p-8 lg:p-10 [direction:ltr]">
          <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white leading-tight">
            {project.title}
          </h3>
          <p className="mt-4 text-cyan-100/75 text-sm sm:text-base leading-relaxed">
            {project.description}
          </p>

          {/* Highlights */}
          <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {project.highlights.map((h) => (
              <li
                key={h}
                className="flex items-center gap-2.5 text-sm text-cyan-50/85"
              >
                <span className={`h-1.5 w-1.5 rounded-full bg-gradient-to-br ${t.ring} flex-shrink-0`} />
                {h}
              </li>
            ))}
          </ul>

          {/* Metrics */}
          <div className={`mt-6 grid grid-cols-2 ${project.metrics.length >= 4 ? "sm:grid-cols-4" : "sm:grid-cols-3"} gap-2`}>
            {project.metrics.map((m) => (
              <div
                key={m.label}
                className="relative gradient-border rounded-xl glass px-3 py-3"
              >
                <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-200/70">
                  {m.label}
                </p>
                <p className={`font-mono mt-1 text-lg font-semibold ${t.text} leading-tight`}>
                  {m.value}
                </p>
                <p className="font-mono text-[10px] text-cyan-200/50 mt-0.5">
                  {m.caption}
                </p>
              </div>
            ))}
          </div>

          {/* Stack + CTA */}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-1.5">
              {project.stack.map((s) => (
                <span
                  key={s}
                  className={`font-mono text-[11px] px-2 py-1 rounded-md border ${t.chip}`}
                >
                  {s}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-5">
              <Link
                to={`/projects/${project.id}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-white hover:text-cyan-300 transition-colors duration-200"
                data-testid={`project-case-study-${project.id}`}
              >
                Read case study
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <a
                href={project.github}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium glass hover:bg-white/10 text-cyan-100 hover:text-white transition-colors duration-200`}
                data-testid={`project-github-${project.id}`}
              >
                <Github className="h-3.5 w-3.5" />
                GitHub
                <ArrowUpRight className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export const Projects = () => {
  return (
    <section
      id="projects"
      className="relative py-24 md:py-32"
      data-testid="projects-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6"
        >
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">
              // 03 — Featured Projects
            </p>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-bold tracking-tighter text-white">
              Engineering,{" "}
              <span className="text-gradient-aurora animate-gradient-pan font-serif italic">
                measured.
              </span>
            </h2>
          </div>
          <p className="max-w-md text-cyan-100/70 text-base leading-relaxed">
            Selected work across LLM inference systems, multi-agent RAG
            architectures, applied ML for security, and research tooling.
          </p>
        </motion.div>

        <div className="mt-16 space-y-10">
          {projects.map((p, i) => (
            <ProjectCard key={p.id} project={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
